import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { useT } from "../i18n";

interface SearchBarProps {
  value: string;
  onSearch: (query: string) => void;
  onTagSelect?: (tag: string) => void;
  placeholder?: string;
}

export function SearchBar({ value, onSearch, onTagSelect, placeholder }: SearchBarProps) {
  const { t } = useT();
  const [input, setInput] = useState(value);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInput(value);
  }, [value]);

  // Debounced tag suggestions (300ms, max 8 results).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (input.trim().length === 0) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const tags = await api.suggestTags(input.trim());
        setSuggestions(tags);
        setShowDropdown(tags.length > 0);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [input]);

  // Close dropdown when clicking outside.
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const commitSearch = useCallback(() => {
    setShowDropdown(false);
    onSearch(input.trim());
  }, [input, onSearch]);

  const selectSuggestion = useCallback(
    (tag: string) => {
      setInput(tag);
      setShowDropdown(false);
      if (onTagSelect) {
        onTagSelect(tag);
      } else {
        onSearch(tag);
      }
    },
    [onSearch, onTagSelect],
  );

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (showDropdown && activeIdx >= 0 && activeIdx < suggestions.length) {
        selectSuggestion(suggestions[activeIdx]);
      } else {
        commitSearch();
      }
      return;
    }
    if (showDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Escape") {
        setShowDropdown(false);
      }
    }
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl mx-auto">
      <div className="flex">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder ?? t("home.search")}
          className="flex-1 px-4 py-2 rounded-l-full bg-brand-surface border border-brand-line focus:border-brand-red outline-none text-sm"
        />
        <button
          className="px-5 py-2 rounded-r-full bg-brand-hover border border-l-0 border-brand-line text-sm hover:bg-brand-line transition-colors"
          onClick={commitSearch}
          aria-label="Search"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        </button>
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-brand-surface border border-brand-line rounded-lg shadow-2xl overflow-hidden">
          {suggestions.map((tag, i) => (
            <button
              key={tag}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                i === activeIdx ? "bg-brand-hover" : "hover:bg-brand-hover"
              }`}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => selectSuggestion(tag)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-brand-muted shrink-0">
                <path d="M5.5,7A1.5,1.5 0 0,1 4,5.5A1.5,1.5 0 0,1 5.5,4A1.5,1.5 0 0,1 7,5.5A1.5,1.5 0 0,1 5.5,7M21.41,11.58L12.41,2.58C12.05,2.22 11.55,2 11,2H4C2.89,2 2,2.89 2,4V11C2,11.55 2.22,12.05 2.59,12.41L11.58,21.41C11.95,21.77 12.45,22 13,22C13.55,22 14.05,21.77 14.41,21.41L21.41,14.41C21.78,14.05 22,13.55 22,13C22,12.44 21.77,11.94 21.41,11.58Z" />
              </svg>
              <span className="text-brand-text">{tag}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
